import React, { useContext, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Card,
  Container,
  Button,
  Fade,
  Divider,
  IconButton,
  LinearProgress,
  CircularProgress,
  TextField
} from "@material-ui/core";
import {
  Call as CallIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  GroupAdd as GroupAddIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Send as SendIcon,
  Message as MessageIcon,
  AccessAlarm as AccessAlarmIcon,
  Timer as TimerIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Timeline as TimelineIcon,
  DateRange as DateRangeIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon
} from "@material-ui/icons";
import { grey, blue, green, orange, red, purple } from "@material-ui/core/colors";
import { isArray, isEmpty } from "lodash";
import moment from "moment";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import clsx from "clsx";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import useDashboard from "../../hooks/useDashboard";
import useContacts from "../../hooks/useContacts";
import useMessages from "../../hooks/useMessages";
import { ChatsUser } from "./ChartsUser";
import { ChartsDate } from "./ChartsDate";
import Filters from "./Filters";
import TableAttendant from "../../components/Dashboard/TableAttendant";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
  },
  headerBox: {
    background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    padding: theme.spacing(4, 3),
    borderRadius: "0 0 30px 30px",
    color: "#fff",
    position: "relative",
    marginBottom: theme.spacing(4),
    boxShadow: "0 10px 30px 0 rgba(0,0,0,0.1)",
    overflow: "hidden",
  },
  headerPattern: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.1,
    backgroundImage: "linear-gradient(45deg, #fff 25%, transparent 25%), linear-gradient(-45deg, #fff 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #fff 75%), linear-gradient(-45deg, transparent 75%, #fff 75%)",
    backgroundSize: "20px 20px",
    animation: "$patternMove 20s linear infinite",
  },
  "@keyframes patternMove": {
    "0%": {
      backgroundPosition: "0 0, 10px 0, 10px -10px, 0px 10px"
    },
    "100%": {
      backgroundPosition: "20px 20px, 30px 20px, 30px 10px, 20px 30px"
    }
  },
  headerTitle: {
    fontSize: "2.5rem",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    position: "relative",
  },
  headerDate: {
    fontSize: "1.1rem",
    opacity: 0.9,
    position: "relative",
  },
  filterButton: {
    borderRadius: 25,
    padding: theme.spacing(1, 3),
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#fff",
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
    }
  },
  refreshButton: {
    marginLeft: theme.spacing(2),
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#fff",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
    }
  },
  cardMetric: {
    height: "100%",
    borderRadius: 20,
    background: "linear-gradient(135deg, #fff 0%, #f5f5f5 100%)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 20px 40px -12px rgba(0,0,0,0.1)",
      "& $cardIcon": {
        transform: "rotate(10deg) scale(1.1)",
      }
    }
  },
  cardContent: {
    padding: theme.spacing(3),
    position: "relative",
    overflow: "hidden",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardIcon: {
    position: "absolute",
    right: -15,
    top: -15,
    fontSize: 100,
    opacity: 0.15,
    transform: "rotate(0deg) scale(1)",
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  cardTitle: {
    fontSize: "1rem",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  cardValue: {
    fontSize: "2.5rem",
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  cardFooter: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardTrend: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 20,
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  trendUp: {
    backgroundColor: alpha(green[500], 0.1),
    color: green[700],
  },
  trendDown: {
    backgroundColor: alpha(red[500], 0.1),
    color: red[700],
  },
  chartContainer: {
    background: "#fff",
    borderRadius: 20,
    padding: theme.spacing(3),
    boxShadow: "0 10px 30px 0 rgba(0,0,0,0.05)",
    height: "100%",
    minHeight: 400,
    display: "flex",
    flexDirection: "column"
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2)
  },
  chartTitle: {
    fontSize: "1.25rem",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
    }
  },
  chartContent: {
    flex: 1,
    minHeight: 300,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
  },
  metricsGrid: {
    marginTop: theme.spacing(4),
  },
  metricBox: {
    padding: theme.spacing(2),
    borderRadius: 15,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  metricIcon: {
    width: 45,
    height: 45,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "& svg": {
      fontSize: 24,
    }
  },
  metricInfo: {
    flex: 1,
  },
  metricValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  metricLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  tableContainer: {
    background: "#fff",
    borderRadius: 20,
    padding: theme.spacing(3),
    boxShadow: "0 10px 30px 0 rgba(0,0,0,0.05)",
    marginTop: theme.spacing(4),
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  filterContainer: {
    background: "#fff",
    borderRadius: 20,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
    boxShadow: "0 10px 30px 0 rgba(0,0,0,0.05)",
  }
}));

const MetricCard = ({ title, value, icon, color, trend, subtitle }) => {
  const classes = useStyles();
  
  return (
    <Card className={classes.cardMetric}>
      <Box className={classes.cardContent}>
        {React.cloneElement(icon, { 
          className: classes.cardIcon,
          style: { color: color }
        })}
        <Typography className={classes.cardTitle}>
          {title}
        </Typography>
        <Typography className={classes.cardValue} style={{ color: color }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="textSecondary">
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Box className={classes.cardFooter}>
            <Box className={clsx(classes.cardTrend, trend >= 0 ? classes.trendUp : classes.trendDown)}>
              {trend >= 0 ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
              <Typography style={{ marginLeft: 4 }}>
                {trend}%
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );
};

const Dashboard = () => {
  const classes = useStyles();
  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [loading, setLoading] = useState(false);
  const { find } = useDashboard();

  const [dateStartTicket, setDateStartTicket] = useState(moment().format("YYYY-MM-DD"));
  const [dateEndTicket, setDateEndTicket] = useState(moment().format("YYYY-MM-DD"));
  const [queueTicket, setQueueTicket] = useState(false);

  const { user } = useContext(AuthContext);

  useEffect(() => {
    let isMounted = true;

    async function firstLoad() {
      if (!isMounted) return;
      setLoading(true);
      try {
        const data = await find({
          date_from: dateStartTicket,
          date_to: dateEndTicket
        });
        if (isMounted) {
          setCounters(data.counters || {});
          setAttendants(isArray(data.attendants) ? data.attendants : []);
        }
      } catch (err) {
        console.error(err);
      }
      if (isMounted) {
        setLoading(false);
      }
    }

    firstLoad();
    const interval = setInterval(firstLoad, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [dateStartTicket, dateEndTicket]);

  async function fetchData() {
    setLoading(true);
    try {
      const data = await find({
        date_from: dateStartTicket,
        date_to: dateEndTicket
      });
      setCounters(data.counters || {});
      setAttendants(isArray(data.attendants) ? data.attendants : []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  function formatTime(minutes) {
    return moment()
      .startOf("day")
      .add(minutes, "minutes")
      .format("HH[h] mm[m]");
  }

  const GetUsers = () => {
    let userOnline = attendants.filter(user => user.online === true).length;
    return userOnline === 0 ? 0 : userOnline;
  };

  const GetContacts = (all) => {
    let props = all ? {} : {
      dateStart: dateStartTicket,
      dateEnd: dateEndTicket,
    };
    const { count } = useContacts(props);
    return count;
  };

  const GetMessages = (all, fromMe) => {
    let props = {
      fromMe,
      ...(all ? {} : {
        dateStart: dateStartTicket,
        dateEnd: dateEndTicket,
      })
    };
    const { count } = useMessages(props);
    return count;
  };

  const calculateProgress = (value, total) => {
    if (!total) return 0;
    return Math.round((value / total) * 100);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.headerBox}>
        <Box className={classes.headerPattern} />
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography className={classes.headerTitle}>
                Panel de Control
              </Typography>
              <Typography className={classes.headerDate}>
                {moment().format("DD [de] MMMM [de] YYYY")}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <IconButton 
                className={classes.refreshButton}
                onClick={fetchData}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Box className={classes.filterContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                label="Fecha Inicial"
                type="date"
                value={dateStartTicket}
                onChange={(e) => setDateStartTicket(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                label="Fecha Final"
                type="date"
                value={dateEndTicket}
                onChange={(e) => setDateEndTicket(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={fetchData}
                disabled={loading}
              >
                Filtrar
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Fade in={true} timeout={300}>
              <div>
                <MetricCard
                  title="En Atención"
                  value={counters.supportHappening || 0}
                  icon={<CallIcon />}
                  color={blue[600]}
                  trend={15}
                  subtitle="Atenciones activas"
                />
              </div>
            </Fade>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Fade in={true} timeout={400}>
              <div>
                <MetricCard
                  title="En Espera"
                  value={counters.supportPending || 0}
                  icon={<HourglassEmptyIcon />}
                  color={orange[600]}
                  trend={8}
                  subtitle="En cola de espera"
                />
              </div>
            </Fade>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Fade in={true} timeout={500}>
              <div>
                <MetricCard
                  title="Agentes En Línea"
                  value={`${GetUsers()}/${attendants.length}`}
                  icon={<RecordVoiceOverIcon />}
                  color={green[600]}
                  trend={calculateProgress(GetUsers(), attendants.length)}
                  subtitle="Disponibles ahora"
                />
              </div>
            </Fade>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Fade in={true} timeout={600}>
              <div>
                <MetricCard
                  title="Finalizados"
                  value={counters.supportFinished || 0}
                  icon={<CheckCircleIcon />}
                  color={purple[500]}
                  trend={20}
                  subtitle="Atenciones completadas"
                />
              </div>
            </Fade>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={3} className={classes.metricsGrid}>
              <Grid item xs={12} sm={6} md={3}>
                <Box className={classes.metricBox}>
                  <Box className={classes.metricIcon}>
                    <SendIcon />
                  </Box>
                  <Box className={classes.metricInfo}>
                    <Typography className={classes.metricValue}>
                      {GetMessages(false, true) || 0}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Mensajes Enviados
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box className={classes.metricBox}>
                  <Box className={classes.metricIcon}>
                    <MessageIcon />
                  </Box>
                  <Box className={classes.metricInfo}>
                    <Typography className={classes.metricValue}>
                      {GetMessages(false, false) || 0}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Mensajes Recibidos
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box className={classes.metricBox}>
                  <Box className={classes.metricIcon}>
                    <AccessTimeIcon />
                  </Box>
                  <Box className={classes.metricInfo}>
                    <Typography className={classes.metricValue}>
                      {formatTime(counters.avgSupportTime || 0)}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Tiempo Promedio de Atención
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box className={classes.metricBox}>
                  <Box className={classes.metricIcon}>
                    <GroupAddIcon />
                  </Box>
                  <Box className={classes.metricInfo}>
                    <Typography className={classes.metricValue}>
                      {GetContacts(false) || 0}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Nuevos Contactos
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box className={classes.chartContainer}>
              <Box className={classes.chartHeader}>
                <Typography className={classes.chartTitle}>
                  <TimelineIcon />
                  Historial de Atenciones
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Última actualización: {moment().format('HH:mm')}
                </Typography>
              </Box>
              <ChatsUser />
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className={classes.chartContainer}>
              <Box className={classes.chartHeader}>
                <Typography className={classes.chartTitle}>
                  <DateRangeIcon />
                  Atenciones por Fecha
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Última actualización: {moment().format('HH:mm')}
                </Typography>
              </Box>
              <ChartsDate />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box className={classes.tableContainer}>
              <Box className={classes.tableHeader}>
                <Typography className={classes.chartTitle}>
                  <PeopleIcon />
                  Agentes
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="caption" color="textSecondary">
                    Total En Línea: {GetUsers()}/{attendants.length}
                  </Typography>
                  {loading && <CircularProgress size={24} />}
                </Box>
              </Box>
              <TableAttendant
                attendants={attendants}
                loading={loading}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard;
